import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TapDatePicker.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Parse a 'YYYY-MM-DD' string as a LOCAL date (not UTC midnight - `new
// Date('2026-08-31')` parses as UTC and can print as the 30th in any
// timezone west of UTC, which is exactly the kind of off-by-one-day bug
// this app has hit before with server-stored dates).
const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * TapDatePicker - a fully tap-driven date picker: tap the field to open a
 * calendar, tap a day to pick it, and tap the header to drill into a month
 * grid or a year grid instead of stepping one month at a time. Nothing
 * requires typing digits, unlike a native <input type="date"> which (on
 * desktop browsers especially) drops you into segmented mm/dd/yyyy fields
 * you fill in by keyboard.
 *
 * @param {string} value - Selected date as 'YYYY-MM-DD', or '' for none
 * @param {function} onChange - Called with the new 'YYYY-MM-DD' string
 * @param {string} placeholder - Shown when no date is selected
 * @param {string} minDate - Optional 'YYYY-MM-DD' floor; earlier days are disabled
 * @param {string} id - id for the trigger button (label htmlFor target)
 */
const TapDatePicker = ({ value = '', onChange, placeholder = 'Select date', minDate = null, id }) => {
  const [open, setOpen] = useState(false);
  // 'days' | 'months' | 'years' - which grid the calendar is currently showing
  const [view, setView] = useState('days');
  const selected = parseLocalDate(value);
  const [cursor, setCursor] = useState(selected || new Date());
  const [popupStyle, setPopupStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  // The popup renders through a portal into document.body with fixed
  // positioning, rather than a plain absolutely-positioned child, because
  // this field commonly sits inside a scrollable list (e.g. the skill tree
  // panel's overflow-y: auto container) - a plain absolute child would get
  // silently clipped by that ancestor's overflow instead of floating above
  // everything, the way a native date picker always does regardless of
  // where it's embedded.
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupWidth = popupRef.current?.offsetWidth || 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 340 && rect.top > 340;
    let left = rect.left;
    if (left + popupWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - popupWidth - 8);
    }
    setPopupStyle({
      position: 'fixed',
      left,
      top: openUpward ? undefined : rect.bottom + 6,
      bottom: openUpward ? window.innerHeight - rect.top + 6 : undefined,
      minWidth: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, view, updatePosition]);

  // Keep the popup glued to the trigger while the page scrolls/resizes
  // (e.g. scrolling the skill list with the calendar open) or close it if
  // the user taps/clicks anywhere outside both the trigger and the popup.
  useEffect(() => {
    if (!open) return;

    const handleOutside = (e) => {
      const inTrigger = containerRef.current && containerRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) {
        setOpen(false);
        setView('days');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (!open) setCursor(selected || new Date());
    setOpen(!open);
    setView('days');
  };

  const min = minDate ? startOfDay(parseLocalDate(minDate)) : null;

  const isDisabled = (date) => !!min && startOfDay(date) < min;

  const pickDay = (day) => {
    const date = new Date(cursor.getFullYear(), cursor.getMonth(), day);
    if (isDisabled(date)) return;
    onChange(toDateString(date));
    setOpen(false);
    setView('days');
  };

  const pickMonth = (monthIdx) => {
    setCursor(new Date(cursor.getFullYear(), monthIdx, 1));
    setView('days');
  };

  const pickYear = (year) => {
    setCursor(new Date(year, cursor.getMonth(), 1));
    setView('months');
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange('');
  };

  // --- Days grid ---
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const firstWeekday = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
  const dayCells = [];
  for (let i = 0; i < firstWeekday; i++) dayCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) dayCells.push(d);

  const today = startOfDay(new Date());

  // --- Years grid: a 12-year window around the cursor's year ---
  const yearWindowStart = Math.floor(cursor.getFullYear() / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => yearWindowStart + i);

  return (
    <div className="tap-date-picker" ref={containerRef}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`tap-date-trigger ${!value ? 'empty' : ''}`}
        onClick={handleToggle}
      >
        <span>{selected ? selected.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : placeholder}</span>
        {value ? (
          <span className="tap-date-clear" onClick={clearDate} title="Clear date">✕</span>
        ) : (
          <span className="tap-date-icon">📅</span>
        )}
      </button>

      {open && popupStyle && createPortal(
        <div className="tap-date-popup" ref={popupRef} style={popupStyle}>
          {view === 'days' && (
            <>
              <div className="tap-date-header">
                <button type="button" className="tap-date-nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>◀</button>
                <div className="tap-date-header-labels">
                  <button type="button" className="tap-date-header-btn" onClick={() => setView('months')}>{MONTHS[cursor.getMonth()]}</button>
                  <button type="button" className="tap-date-header-btn" onClick={() => setView('years')}>{cursor.getFullYear()}</button>
                </div>
                <button type="button" className="tap-date-nav" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>▶</button>
              </div>

              <div className="tap-date-weekdays">
                {WEEKDAYS.map(w => <div key={w} className="tap-date-weekday">{w}</div>)}
              </div>

              <div className="tap-date-grid">
                {dayCells.map((day, idx) => {
                  if (day === null) return <div key={idx} className="tap-date-cell empty" />;
                  const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
                  const disabled = isDisabled(cellDate);
                  const isToday = startOfDay(cellDate).getTime() === today.getTime();
                  const isSelected = selected && startOfDay(cellDate).getTime() === startOfDay(selected).getTime();
                  return (
                    <button
                      type="button"
                      key={idx}
                      className={`tap-date-cell day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                      disabled={disabled}
                      onClick={() => pickDay(day)}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {view === 'months' && (
            <>
              <div className="tap-date-header">
                <button type="button" className="tap-date-nav" onClick={() => setCursor(new Date(cursor.getFullYear() - 1, cursor.getMonth(), 1))}>◀</button>
                <button type="button" className="tap-date-header-btn" onClick={() => setView('years')}>{cursor.getFullYear()}</button>
                <button type="button" className="tap-date-nav" onClick={() => setCursor(new Date(cursor.getFullYear() + 1, cursor.getMonth(), 1))}>▶</button>
              </div>
              <div className="tap-date-grid months">
                {MONTHS.map((m, idx) => (
                  <button
                    type="button"
                    key={m}
                    className={`tap-date-cell month ${idx === cursor.getMonth() ? 'selected' : ''}`}
                    onClick={() => pickMonth(idx)}
                  >
                    {m.slice(0, 3)}
                  </button>
                ))}
              </div>
            </>
          )}

          {view === 'years' && (
            <>
              <div className="tap-date-header">
                <button type="button" className="tap-date-nav" onClick={() => setCursor(new Date(cursor.getFullYear() - 12, cursor.getMonth(), 1))}>◀</button>
                <span className="tap-date-header-labels tap-date-header-static">{years[0]} – {years[years.length - 1]}</span>
                <button type="button" className="tap-date-nav" onClick={() => setCursor(new Date(cursor.getFullYear() + 12, cursor.getMonth(), 1))}>▶</button>
              </div>
              <div className="tap-date-grid years">
                {years.map(year => (
                  <button
                    type="button"
                    key={year}
                    className={`tap-date-cell year ${year === cursor.getFullYear() ? 'selected' : ''}`}
                    onClick={() => pickYear(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default TapDatePicker;
